import { useEffect, useRef, useState } from "react";

import { buildInitialMediaState } from "./catchMediaPipeline";
import { buildPreviewSource } from "./catchMediaPreview";
import useCatchMediaApplyFlow from "./useCatchMediaApplyFlow";
import useCatchMediaCleanup from "./useCatchMediaCleanup";
import useCatchMediaSelectionFlow from "./useCatchMediaSelectionFlow";

export default function useCatchMediaState({
  draftCatchMedia,
  setDraftCatchMedia,
  selectionIdRef,
  submitTransactionRef,
  setSubmitPhase,
  setSubmitStatus,
  t,
  user,
  requestExifConsent,
  resetSelectionDerivedConsentState,
  resetSelectionDerivedLocationState,
  applyDetectedExifDate,
  applyDetectedExifTime,
  getManualDateTimeEditVersion,
  setExifGps,
  setCoordsFromExifIfEmpty,
}) {
  const [mediaState, setMediaState] = useState(() => {
    const initial = buildInitialMediaState(draftCatchMedia);
    selectionIdRef.current = initial.selectionId || 0;
    return initial;
  });
  const snapshotInFlightRef = useRef(null);

  const {
    selectedFile,
    snapshotFile,
    snapshotStatus,
    mediaType: selectedMediaType,
    previewUrl: imagePreviewUrl,
    submitReady,
    blockingError,
    nonBlockingMessage,
  } = mediaState;

  const cleanup = useCatchMediaCleanup({
    mediaState,
    imagePreviewUrl,
    setMediaState,
    setDraftCatchMedia,
    selectionIdRef,
    snapshotInFlightRef,
    setSubmitPhase,
    resetSelectionDerivedConsentState,
    resetSelectionDerivedLocationState,
  });
  const { updateMediaState, uploadAssetRef } = cleanup;

  const applyFlow = useCatchMediaApplyFlow({
    selectionIdRef,
    uploadAssetRef,
    setMediaStateForSelection: cleanup.setMediaStateForSelection,
    updateMediaState,
    clearDeferredFileInput: cleanup.clearDeferredFileInput,
    rejectImageSelectionWithoutSnapshot:
      cleanup.rejectImageSelectionWithoutSnapshot,
    setSubmitPhase,
    applyDetectedExifDate,
    applyDetectedExifTime,
    getManualDateTimeEditVersion,
    setExifGps,
    selectedFile,
    snapshotFile,
    snapshotStatus,
    selectedMediaType,
    imagePreviewUrl,
  });

  const selectionFlow = useCatchMediaSelectionFlow({
    selectionIdRef,
    submitTransactionRef,
    snapshotInFlightRef,
    setSubmitStatus,
    t,
    resetSelectionDerivedMediaState: cleanup.resetSelectionDerivedMediaState,
    setMediaStateForSelection: cleanup.setMediaStateForSelection,
    uploadAssetRef,
    rejectImageSelectionWithoutSnapshot:
      cleanup.rejectImageSelectionWithoutSnapshot,
    finalizeSelectionInputClear: cleanup.finalizeSelectionInputClear,
    setSubmitPhase,
    applySelectedMedia: applyFlow.applySelectedMedia,
    user,
    requestExifConsent,
    setCoordsFromExifIfEmpty,
  });

  useEffect(() => {
    if (selectedMediaType !== "video") {
      return undefined;
    }

    const previewFile = snapshotFile || selectedFile;
    if (!previewFile || imagePreviewUrl || mediaState.previewStatus === "pending") {
      return undefined;
    }

    let cancelled = false;
    const selectionId = mediaState.selectionId;
    const previewInputKind =
      snapshotFile && snapshotStatus === "success" ? "snapshot" : "original_file";

    buildPreviewSource(previewFile, "video", {
      selectionId,
      previewInputKind,
    })
      .then((nextPreview) => {
        if (cancelled || selectionIdRef.current !== selectionId || !nextPreview?.url) {
          return;
        }

        updateMediaState(selectionId, (prev) => {
          if (prev.previewUrl) {
            return prev;
          }

          return {
            ...prev,
            previewUrl: nextPreview.url,
            previewSource: nextPreview.previewSource,
            previewStatus: nextPreview.previewStatus || "ready",
          };
        });
        uploadAssetRef.current = {
          ...uploadAssetRef.current,
          previewStatus: nextPreview.previewStatus || "ready",
          previewBlobUrl: nextPreview.url,
        };
      })
      .catch((error) => {
        if (cancelled || selectionIdRef.current !== selectionId) {
          return;
        }

        console.warn("[CATCH_VIDEO_DEBUG]", {
          stage: "video_preview_restore_failed",
          selectionId,
          errorName: typeof error?.name === "string" ? error.name : "",
          errorMessage:
            typeof error?.message === "string" ? error.message : "",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    imagePreviewUrl,
    mediaState.previewStatus,
    mediaState.selectionId,
    selectedFile,
    selectedMediaType,
    selectionIdRef,
    snapshotFile,
    snapshotStatus,
    updateMediaState,
    uploadAssetRef,
  ]);

  return {
    mediaState,
    mediaStateRef: cleanup.mediaStateRef,
    uploadAssetRef: cleanup.uploadAssetRef,
    selectedFile,
    snapshotFile,
    snapshotStatus,
    selectedMediaType,
    imagePreviewUrl,
    submitReady,
    mediaError: blockingError,
    previewError: nonBlockingMessage,
    updateMediaState: cleanup.updateMediaState,
    clearMediaState: cleanup.clearMediaState,
    rejectImageSelectionWithoutSnapshot:
      cleanup.rejectImageSelectionWithoutSnapshot,
    applySelectedMedia: applyFlow.applySelectedMedia,
    handleImageUpload: selectionFlow.handleImageUpload,
    handleImagePreviewError: applyFlow.handleImagePreviewError,
  };
}
